import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { FontSize } from "../../../components/Text";
import { DashboardMode } from "../../../model/dashboard/types/DashboardMode.enum";
import { TestIDs } from "../../../tests/types/testIds";
import { Rounded } from "../../../theme/Rounded";
import { Shadow } from "../../../theme/Shadow";
import { Spacing } from "../../../theme/Spacing";

const Group = styled.div<{ $block?: boolean }>`
  display: ${({ $block }) => ($block ? "flex" : "inline-flex")};
  padding: 3px;
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.sub};

  & > button {
    flex: ${({ $block }) => ($block ? 1 : "none")};
  }
`;

const Option = styled.button<{ $active: boolean }>`
  all: unset;
  box-sizing: border-box;
  text-align: center;
  cursor: pointer;
  padding: ${Spacing.xs} ${Spacing.m};
  border-radius: ${Rounded.pill};
  font-size: ${FontSize.s};
  font-weight: ${({ $active }) => ($active ? "bold" : "normal")};
  white-space: nowrap;
  color: ${({ theme, $active }) => ($active ? theme.main : theme.darkerSub)};
  background-color: ${({ theme, $active }) =>
    $active ? theme.white : theme.transparent};
  box-shadow: ${({ $active }) => ($active ? Shadow.SM : "none")};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.main};
    outline-offset: 1px;
  }
`;

type DashboardModeToggleProps = {
  mode: DashboardMode;
  onChange: ATVoidFunction<DashboardMode>;
  block?: boolean;
  /** Only one rendered copy may carry the e2e ids */
  withTestIds?: boolean;
};

export const DashboardModeToggle: React.FC<DashboardModeToggleProps> = ({
  mode,
  onChange,
  block,
  withTestIds = true,
}) => {
  const { t } = useTranslation();

  return (
    <Group
      role="group"
      aria-label={t("dashboard.mode.label")}
      $block={block}
    >
      {[DashboardMode.SETUP, DashboardMode.TODAY].map((option) => (
        <Option
          key={option}
          type="button"
          $active={mode === option}
          aria-pressed={mode === option}
          onClick={() => onChange(option)}
          data-testid={
            withTestIds ? TestIDs.dashboard.mode(option) : undefined
          }
        >
          {t(`dashboard.mode.${option}`)}
        </Option>
      ))}
    </Group>
  );
};
